import React, { useMemo, useState } from 'react'
import YouTube from 'react-youtube'
import "./mdm.css"
import { MdSearch, MdClose } from 'react-icons/md'
import { mdmSections, PLATFORMS } from './MdmData'
import MdmIcon from './MdmIcon'
import MdmCardModal from './MdmCardModal'
import MdmTabs from './MdmTabs'
import { formatDuration } from './mdmUtils'

const PlatformPill = ({ platform }) => {
    if (!platform) return null
    return <span className={`mdm-pill mdm-plat-${platform}`}>{PLATFORMS[platform].full}</span>
}

const MdmCards = () => {

    const [search, setSearch] = useState("")
    const [activeCard, setActiveCard] = useState(null)
    const [activeSection, setActiveSection] = useState(null)
    const [durations, setDurations] = useState({})

    const videoIds = useMemo(() => (
        [...new Set(mdmSections.flatMap(s => s.cards).filter(c => c.videoId).map(c => c.videoId))]
    ), [])

    const query = search.trim().toLowerCase()

    const filteredSections = useMemo(() => {
        const matchesQuery = (card) => {
            if (!query) return true
            const haystack = [card.t, card.d].join(" ").toLowerCase()
            return haystack.includes(query)
        }
        return mdmSections
            .map(section => ({
                ...section,
                cards: section.cards.filter(matchesQuery),
            }))
            .filter(section => section.cards.length > 0)
    }, [query])

    const totalShown = filteredSections.reduce((acc, s) => acc + s.cards.length, 0)
    const totalCards = mdmSections.reduce((acc, s) => acc + s.cards.length, 0)

    const handleOpen = (card, section) => {
        setActiveCard(card)
        setActiveSection(section)
    }
    const handleClose = () => setActiveCard(null)

    const clearSearch = () => setSearch("")

    return (
        <div className="mdm-browser">
            <div className="mdm-topbar">
                <div className="mdm-topbar-titlewrap">
                    <h4>Mobile Device Management</h4>
                    <p className="mdm-topbar-subtitle">Enroll, secure and manage devices across Android, iOS and Windows.</p>
                </div>
                <div className="mdm-search">
                    <MdSearch className="mdm-search-icon" />
                    <input
                        placeholder="Search modules — try enrollment, kiosk, geofencing"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Escape' && search) { e.stopPropagation(); clearSearch() } }}
                        aria-label="Search modules"
                    />
                    {search &&
                        <button
                            type="button"
                            className="mdm-search-clear"
                            onClick={clearSearch}
                            aria-label="Clear search"
                        >
                            <MdClose />
                        </button>
                    }
                </div>
            </div>

            <div className="mdm-filterrow">
                <MdmTabs active="all" />
                <span className="mdm-tally">{totalShown} of {totalCards} shown</span>
            </div>

            {filteredSections.length === 0 &&
                <div className="mdm-empty">
                    <h6>No modules match "{search}"</h6>
                    <p className="mb-0 mt-2">Try a different term, or clear your search.</p>
                    <button type="button" className="mdm-empty-reset" onClick={clearSearch}>
                        Clear search
                    </button>
                </div>
            }

            {filteredSections.map((section) => (
                <div className="mdm-section" key={section.id}>
                    <div className="mdm-section-head">
                        <h6>{section.title}</h6>
                        <span className="mdm-section-note">{section.note}</span>
                        <span className="mdm-section-tally">{section.cards.length} {section.cards.length === 1 ? "module" : "modules"}</span>
                    </div>
                    <div className="row g-4">
                        {section.cards.map((card, cardIndex) => (
                            <div key={cardIndex} className="col-sm-12 col-md-6 col-lg-4 col-xl-3">
                                <button
                                    className={`mdm-custom-shadow ${card.soon ? "mdm-card-soon" : ""}`}
                                    onClick={() => handleOpen(card, section)}
                                >
                                    <div className="mdm-card-icon">
                                        <MdmIcon name={card.icon} color="rgb(32, 31, 31)" />
                                    </div>
                                    <div className="mdm-card-title">{card.t}</div>
                                    <div className="mdm-card-desc">{card.d}</div>
                                    <div className="mdm-card-foot">
                                        <PlatformPill platform={card.platform} />
                                        {card.soon
                                            ? <span className="mdm-soon-badge">Coming soon</span>
                                            : (durations[card.videoId] && <span className="mdm-mins">{durations[card.videoId]}</span>)
                                        }
                                    </div>
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            ))}

            {activeCard &&
                <MdmCardModal card={activeCard} section={activeSection} onClose={handleClose} />
            }

            <div style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', opacity: 0, pointerEvents: 'none' }} aria-hidden="true">
                {videoIds.map(id => (
                    <YouTube
                        key={id}
                        videoId={id}
                        opts={{ height: '90', width: '160', playerVars: { autoplay: 0 } }}
                        onReady={(e) => setDurations(prev => ({ ...prev, [id]: formatDuration(e.target.getDuration()) }))}
                    />
                ))}
            </div>
        </div>
    )
}

export default MdmCards
